import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Pause, Play, Wifi, WifiOff,
  LogIn, LogOut, UserPlus, FileText, Edit, Trash2, 
  Printer, KeyRound, AlertTriangle, FileDown, Upload as UploadIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface LiveEvent {
  id: string;
  username: string | null;
  event_type: string;
  event_category: string;
  description: string | null;
  success: boolean;
  created_at: string;
}

const EVENT_ICONS: Record<string, React.ReactNode> = {
  'login': <LogIn className="w-3.5 h-3.5" />,
  'logout': <LogOut className="w-3.5 h-3.5" />,
  'user_created': <UserPlus className="w-3.5 h-3.5" />,
  'template_created': <FileText className="w-3.5 h-3.5" />,
  'template_updated': <Edit className="w-3.5 h-3.5" />,
  'template_deleted': <Trash2 className="w-3.5 h-3.5" />,
  'template_saved': <FileText className="w-3.5 h-3.5" />,
  'print': <Printer className="w-3.5 h-3.5" />,
  'kiosk_print': <Printer className="w-3.5 h-3.5" />,
  'batch_export': <FileDown className="w-3.5 h-3.5" />,
  'pdf_export': <FileDown className="w-3.5 h-3.5" />,
  'csv_import': <UploadIcon className="w-3.5 h-3.5" />,
  'password_changed': <KeyRound className="w-3.5 h-3.5" />,
};

const CATEGORY_COLORS: Record<string, string> = {
  'auth': 'border-l-primary',
  'template': 'border-l-accent',
  'user': 'border-l-secondary',
  'print': 'border-l-muted-foreground',
  'error': 'border-l-destructive',
};

export const LiveActivityFeed: React.FC = () => {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [paused, setPaused] = useState(false);
  const [connected, setConnected] = useState(false);
  const pausedRef = useRef(paused);
  const bufferRef = useRef<LiveEvent[]>([]);

  useEffect(() => {
    pausedRef.current = paused;
    if (!paused && bufferRef.current.length > 0) {
      setEvents(prev => [...bufferRef.current, ...prev].slice(0, 50));
      bufferRef.current = [];
    }
  }, [paused]);

  // Load initial recent events
  useEffect(() => {
    const loadRecent = async () => {
      const { data } = await supabase
        .from('audit_logs')
        .select('id, username, event_type, event_category, description, success, created_at')
        .order('created_at', { ascending: false })
        .limit(20);
      if (data) {
        setEvents(data as LiveEvent[]);
      }
    };
    loadRecent();
  }, []);

  // Subscribe to realtime
  useEffect(() => {
    const channel = supabase
      .channel('live-activity-feed')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'audit_logs' },
        (payload) => {
          const newEvent = payload.new as LiveEvent;
          if (pausedRef.current) {
            bufferRef.current = [newEvent, ...bufferRef.current].slice(0, 50);
          } else {
            setEvents(prev => [newEvent, ...prev].slice(0, 50));
          }
        }
      )
      .subscribe((status) => {
        setConnected(status === 'SUBSCRIBED');
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium">Live Activity Feed</h3>
          <Badge variant={connected ? 'default' : 'destructive'} className="text-[10px] gap-1 px-1.5 py-0">
            {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {connected ? 'Live' : 'Disconnected'}
          </Badge>
          {paused && bufferRef.current.length > 0 && (
            <Badge variant="secondary" className="text-[10px]">
              {bufferRef.current.length} buffered
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setPaused(!paused)}
          className="h-7 gap-1.5 text-xs"
        >
          {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
          {paused ? 'Resume' : 'Pause'}
        </Button>
      </div>

      <ScrollArea className="h-[400px]">
        <div className="space-y-1">
          {events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Waiting for activity...
            </div>
          ) : (
            events.map((event, index) => (
              <div
                key={event.id}
                className={cn(
                  'flex items-start gap-3 px-3 py-2 rounded-md border-l-2 bg-card/50 transition-all',
                  CATEGORY_COLORS[event.event_category] || 'border-l-muted',
                  !event.success && 'bg-destructive/5',
                  index === 0 && !paused && 'animate-in slide-in-from-top-2 duration-300'
                )}
              >
                <div className="mt-0.5 text-muted-foreground">
                  {EVENT_ICONS[event.event_type] || <AlertTriangle className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium truncate">
                      {event.username || 'System'}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {event.event_type.replace(/_/g, ' ')}
                    </span>
                    {!event.success && (
                      <Badge variant="destructive" className="text-[9px] px-1 py-0 h-3.5">
                        Failed
                      </Badge>
                    )}
                  </div>
                  {event.description && (
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {event.description}
                    </p>
                  )}
                </div>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                  {format(new Date(event.created_at), 'HH:mm:ss')}
                </span>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
